import { getLorebookCardDisplayData } from "@next-platform/client-common-core/lorebook/cardDisplay";
import isAttachedLorebookTemporarilyUnavailable from "@next-platform/client-common-core/utils/isAttachedLorebookTemporarilyUnavailable";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  LorebookCard,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import type { Lorebook } from "@next-platform/packages-ts-types";

import Trans from "@/components/Trans/Trans";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

interface ILorebookSectionProps {
  attachedLorebook?: Lorebook | null;
  onAttach?: () => void;
  onReplace?: () => void;
  onDetach?: () => void;
  disabled?: boolean;
  dataTestId?: string;
  cardVariant?: "attached" | "static";
  /** The group's Lorebook was removed without its owner doing it (made private by its creator, or deleted); shown until another Lorebook is attached. */
  isAutoDetached?: boolean;
}

const TemporarilyUnavailableMessage = () => (
  <div data-testid="LorebookSection-TemporarilyUnavailableMessage">
    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
      <Trans
        i18nKey="chatbot:form.section.lorebook.underReviewUnavailable"
        defaults="This Lorebook is flagged by the moderation team and temporarily unavailable. It remains attached but won’t apply until approved."
      />
    </Text>
  </div>
);

const AutoDetachedMessage = () => (
  <div data-testid="LorebookSection-AutoDetachedMessage">
    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
      <Trans
        i18nKey="createGroup:form.field.lorebook.autoDetached"
        defaults="The previously attached Lorebook is no longer available and has been detached."
      />
    </Text>
  </div>
);

const LorebookSectionActions = ({
  hasAttachedLorebook,
  onAttach,
  onReplace,
  onDetach,
  disabled,
}: {
  hasAttachedLorebook: boolean;
  onAttach?: () => void;
  onReplace?: () => void;
  onDetach?: () => void;
  disabled?: boolean;
}) => (
  <div className="flex flex-row gap-[7px]">
    <Button
      variant={ButtonVariant.OUTLINED}
      size={ButtonSize.MEDIUM}
      iconStart={hasAttachedLorebook ? IconVariants.REFRESH : IconVariants.PLUS}
      onClick={hasAttachedLorebook ? onReplace : onAttach}
      disabled={disabled}
      type="button"
      className="rounded-[7px] border-gray-7"
    >
      {hasAttachedLorebook ? (
        <Trans i18nKey="chatbot:form.section.lorebook.button.replace" />
      ) : (
        <Trans i18nKey="chatbot:form.section.lorebook.button.attach" />
      )}
    </Button>
    {hasAttachedLorebook ? (
      <Button
        variant={ButtonVariant.OUTLINED}
        size={ButtonSize.MEDIUM}
        iconStart={IconVariants.TRASH}
        onClick={onDetach}
        disabled={disabled}
        type="button"
        className="w-11 rounded-[7px] border-gray-7 px-0"
      />
    ) : null}
  </div>
);

export default function LorebookSection({
  attachedLorebook,
  onAttach,
  onReplace,
  onDetach,
  disabled,
  dataTestId = "",
  cardVariant = "attached",
  isAutoDetached = false,
}: ILorebookSectionProps) {
  const hasAttachedLorebook = Boolean(attachedLorebook?.id);
  const shouldShowTemporarilyUnavailable = hasAttachedLorebook && isAttachedLorebookTemporarilyUnavailable(attachedLorebook);
  const attachedLorebookDisplayData = attachedLorebook ? getLorebookCardDisplayData(attachedLorebook) : null;
  const avatarUrl = attachedLorebookDisplayData?.avatarUrl
    ? (getAvatarUrl(attachedLorebookDisplayData.avatarUrl, { returnWithOriginalRatio: true }) as string)
    : "";

  return (
    <div className="flex flex-col gap-2" data-testid={dataTestId ?? ""}>
      <div className="flex items-center gap-1">
        <Text variant={TextVariants.LABEL_LARGE_MEDIUM} as="span" className="text-gray-12">
          <Trans i18nKey="chatbot:form.section.lorebook.label" />
        </Text>
        <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="text-gray-11" />
      </div>

      {hasAttachedLorebook && attachedLorebookDisplayData ? (
        <LorebookCard
          variant={cardVariant}
          dataTestId="lorebook-card"
          name={attachedLorebookDisplayData.name}
          description={attachedLorebookDisplayData.description}
          creatorUsername={attachedLorebookDisplayData.creatorUsername}
          numEntries={attachedLorebookDisplayData.numEntries}
          isPrivate={attachedLorebookDisplayData.isPrivate}
          avatarUrl={avatarUrl}
        />
      ) : null}

      {shouldShowTemporarilyUnavailable ? <TemporarilyUnavailableMessage /> : null}

      <LorebookSectionActions
        hasAttachedLorebook={hasAttachedLorebook}
        onAttach={onAttach}
        onReplace={onReplace}
        onDetach={onDetach}
        disabled={disabled}
      />

      {isAutoDetached && !hasAttachedLorebook ? <AutoDetachedMessage /> : null}

      <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
        <Trans i18nKey="chatbot:form.section.lorebook.emptyDescription" />
      </Text>
    </div>
  );
}
